/**
 * Контракты коэффициентных справочников методик (E04.6, ФТ-3.4).
 *
 * Три типа ключа: числовой диапазон, категория, многомерный составной ключ.
 * Значения — decimal-строки: через float теряется точность (НФТ-12).
 */

import { z } from "zod";
import { decimalStringSchema } from "./catalog";
import { apiSuccessSchema } from "./envelope";

const nameSchema = z.string().trim().min(1).max(255);

/** Тип ключа справочника (Архитектура 7.4). */
export const referenceKeyTypeSchema = z.enum(["range", "category", "multi"]);

export type ReferenceKeyType = z.infer<typeof referenceKeyTypeSchema>;

/** Ключ диапазона: длина 0–50 → коэффициент (приближённое совпадение — в вычислителе). */
export const rangeReferenceKeySchema = z
  .object({
    min: z.number(),
    max: z.number(),
  })
  .refine((key) => key.min <= key.max, { message: "Нижняя граница диапазона не больше верхней" });

export type RangeReferenceKey = z.infer<typeof rangeReferenceKeySchema>;

/** Ключ категории: «сухо» / «влажно» / «пыльно». */
export const categoryReferenceKeySchema = z.object({
  category: z.string().trim().min(1).max(255),
});

export type CategoryReferenceKey = z.infer<typeof categoryReferenceKeySchema>;

/** Составной ключ: типоразмер × угол × скорость × тип планки. */
export const multiReferenceKeySchema = z
  .object({
    dims: z.record(z.string().min(1), z.union([z.string(), z.number()])),
  })
  .refine((key) => Object.keys(key.dims).length > 0, {
    message: "Многомерный ключ должен содержать хотя бы одно измерение",
  });

export type MultiReferenceKey = z.infer<typeof multiReferenceKeySchema>;

export const referenceKeySchema = z.union([
  rangeReferenceKeySchema,
  categoryReferenceKeySchema,
  multiReferenceKeySchema,
]);

export type ReferenceKey = z.infer<typeof referenceKeySchema>;

/** Строка справочника. */
export const referenceValueSchema = z.object({
  id: z.string().uuid(),
  key: referenceKeySchema,
  value: decimalStringSchema,
});

export type ReferenceValue = z.infer<typeof referenceValueSchema>;

const referenceValueInputSchema = z.object({
  key: z.any(),
  value: decimalStringSchema,
});

/** Коэффициентный справочник методики. */
export const referenceTableSchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  keyType: referenceKeyTypeSchema,
  methodologyId: z.string().uuid(),
  methodologyName: nameSchema,
  values: z.array(referenceValueSchema),
  valueCount: z.number().int().nonnegative(),
});

export type ReferenceTable = z.infer<typeof referenceTableSchema>;

/** Тело `POST /api/v1/admin/reference-tables`. */
export const createReferenceTableRequestSchema = z.object({
  name: nameSchema,
  keyType: referenceKeyTypeSchema,
  methodologyId: z.string().uuid(),
  values: z.array(referenceValueInputSchema).default([]),
});

export type CreateReferenceTableRequest = z.infer<typeof createReferenceTableRequestSchema>;

/** Тело `PATCH /api/v1/admin/reference-tables/{id}`. Тип ключа не меняется — иначе все строки станут невалидны. */
export const updateReferenceTableRequestSchema = z
  .object({
    name: nameSchema.optional(),
    values: z.array(referenceValueInputSchema).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: "Нет полей для изменения" });

export type UpdateReferenceTableRequest = z.infer<typeof updateReferenceTableRequestSchema>;

export const referenceTableListResponseSchema = apiSuccessSchema(z.array(referenceTableSchema));
export const referenceTableResponseSchema = apiSuccessSchema(referenceTableSchema);

/** Разбирает и проверяет ключ под конкретный тип таблицы. */
export function parseReferenceKey(keyType: ReferenceKeyType, key: unknown): ReferenceKey {
  if (keyType === "range") {
    return rangeReferenceKeySchema.parse(key);
  }
  if (keyType === "category") {
    return categoryReferenceKeySchema.parse(key);
  }
  return multiReferenceKeySchema.parse(key);
}
